import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Zap, Flame, TrendingUp, TrendingDown, Activity, ArrowUpRight, ArrowDownRight, Lock, Star, EyeOff } from "lucide-react";
import { rrColor, winColor } from "@/lib/signalColors";
import { formatHoursShort, heatBadgeMeta } from "@/lib/format";

const STYLE_META = {
  scalp: { label: "SCALP", color: "#FF3B30", icon: Zap, hint: "5m – 1h" },
  day:   { label: "DAY",   color: "#F59E0B", icon: Flame, hint: "1h – 1d" },
  swing: { label: "SWING", color: "#00E0FF", icon: Activity, hint: "3d – 3w" },
};

const fmtPrice = (n) => {
  if (n === undefined || n === null) return "-";
  if (n < 1) return n.toFixed(4);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

function MomentumStrip({ data, navigate }) {
  if (!data) return null;
  const sections = [
    { key: "top_gainers",   title: "Top Gainers",    icon: TrendingUp,   color: "#00FFA3", metric: (a) => `${a.change_24h >= 0 ? '+' : ''}${a.change_24h}%` },
    { key: "top_losers",    title: "Top Losers",     icon: TrendingDown, color: "#FF3B30", metric: (a) => `${a.change_24h}%` },
    { key: "volume_spikes", title: "Volume Spikes",  icon: Activity,     color: "#00E0FF", metric: (a) => `${a.volume_ratio}x avg` },
    { key: "most_volatile", title: "Most Volatile",  icon: Flame,        color: "#F59E0B", metric: (a) => `${a.change_24h >= 0 ? '+' : ''}${a.change_24h}%` },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="momentum-scanner">
      {sections.map((sec) => {
        const Icon = sec.icon;
        return (
          <Card key={sec.key} className="glass-effect border-white/5">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4" style={{ color: sec.color }} />
                <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold">{sec.title}</span>
              </div>
              <div className="space-y-1.5">
                {(data[sec.key] || []).slice(0, 5).map((a) => (
                  <button
                    key={`${sec.key}-${a.symbol}`}
                    data-testid={`momentum-row-${sec.key}-${a.symbol}`}
                    onClick={() => navigate(`/asset/${a.symbol}`)}
                    className="w-full flex items-center justify-between text-xs hover:bg-white/5 rounded px-2 py-1 transition-colors"
                  >
                    <span className="font-bold text-white mono">{a.symbol}</span>
                    <span
                      className="mono font-semibold"
                      style={{ color: sec.color }}
                    >
                      {sec.metric(a)}
                    </span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function SignalRow({ sig, isAuthenticated, onTakeTrade, onNavigate, onHide, boostTtlHours = 168 }) {
  const meta = STYLE_META[sig.style] || STYLE_META.day;
  const StyleIcon = meta.icon;
  const isBuy = sig.signal.includes("BUY");

  // Boost decay: full opacity early, fades to ~0.35 as TTL approaches
  const boostAge = sig.boost_age_hours ?? 0;
  const boostProgress = Math.min(boostAge / boostTtlHours, 1);
  const boostOpacity = Math.max(0.35, 1 - boostProgress * 0.65);
  const hoursLeft = Math.max(0, boostTtlHours - boostAge);
  const decayLabel = formatHoursShort(hoursLeft);

  const heatMeta = heatBadgeMeta(sig.heat_score || 0);
  return (
    <Card
      data-testid={`day-signal-card-${sig.style}-${sig.symbol}`}
      className={`glass-effect cursor-pointer transition-all duration-300 ${
        isBuy ? "hover:border-[#00FFA3]/30" : "hover:border-[#FF3B30]/30"
      }`}
      onClick={() => onNavigate(`/asset/${sig.symbol}`)}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1 shrink-0"
              style={{ backgroundColor: `${meta.color}20`, color: meta.color, border: `1px solid ${meta.color}30` }}
            >
              <StyleIcon className="w-3 h-3" />
              {meta.label}
            </div>
            <div
              className={`px-2 py-0.5 rounded text-[10px] font-bold shrink-0 ${
                sig.signal === "STRONG BUY"
                  ? "bg-[#00FFA3]/20 text-[#00FFA3] border border-[#00FFA3]/30"
                  : sig.signal === "BUY"
                  ? "bg-[#00FFA3]/10 text-[#00FFA3]"
                  : sig.signal === "STRONG SELL"
                  ? "bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/30"
                  : "bg-[#FF3B30]/10 text-[#FF3B30]"
              }`}
            >
              {sig.signal}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm sm:text-base font-bold text-white mono">{sig.symbol}</span>
                {sig.priority_boost && (
                  <span
                    data-testid={`boosted-${sig.symbol}`}
                    style={{ opacity: boostOpacity }}
                    className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#00FFA3]/20 text-[#00FFA3] border border-[#00FFA3]/30 transition-opacity duration-300"
                    title={`Priority-boosted — decays in ${decayLabel}`}
                  >
                    ★ BOOSTED · {decayLabel}
                  </span>
                )}
                {heatMeta && (
                  <span
                    data-testid={`heat-${sig.symbol}`}
                    className={`px-1.5 py-0.5 rounded text-[9px] font-bold border inline-flex items-center gap-1 ${heatMeta.bg} ${heatMeta.text} ${heatMeta.border}`}
                    title={`Signal heat ${sig.heat_score}/100 — ${(sig.heat_reasons || []).join(", ")}`}
                  >
                    <Flame className="w-2.5 h-2.5" />
                    {heatMeta.label}
                  </span>
                )}
                <span className="text-[10px] text-gray-500">{sig.timeframe_label}</span>
              </div>
              <p className="text-[11px] text-gray-500 truncate">{sig.name}</p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm sm:text-base font-bold text-white mono">${fmtPrice(sig.current_price)}</div>
            <div className={`text-[11px] mono ${sig.change_24h >= 0 ? "text-[#00FFA3]" : "text-[#FF3B30]"}`}>
              {sig.change_24h >= 0 ? "+" : ""}{sig.change_24h}%
            </div>
          </div>
        </div>

        {/* Entry/Target/Stop */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 py-2 border-t border-b border-white/5">
          <div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">Entry</div>
            <div className="text-xs sm:text-sm font-bold text-[#00E0FF] mono">${fmtPrice(sig.entry_price)}</div>
          </div>
          <div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5 flex items-center gap-1">
              Target <ArrowUpRight className="w-3 h-3 text-[#00FFA3]" />
            </div>
            <div className="text-xs sm:text-sm font-bold text-[#00FFA3] mono">${fmtPrice(sig.target_price)}</div>
          </div>
          <div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5 flex items-center gap-1">
              Stop <ArrowDownRight className="w-3 h-3 text-[#FF3B30]" />
            </div>
            <div className="text-xs sm:text-sm font-bold text-[#FF3B30] mono">${fmtPrice(sig.stop_loss)}</div>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-4 gap-2 pt-2">
          <div>
            <div className="text-[10px] text-gray-500 mb-0.5">R:R</div>
            <div className={`text-xs font-bold mono ${rrColor(sig.risk_reward)}`}>
              1:{sig.risk_reward}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-gray-500 mb-0.5">Win</div>
            <div className={`text-xs font-bold mono ${winColor(sig.win_probability)}`}>
              {sig.win_probability}%
            </div>
          </div>
          <div>
            <div className="text-[10px] text-gray-500 mb-0.5">Return</div>
            <div className="text-xs font-bold text-[#00FFA3] mono">+{sig.expected_return}%</div>
          </div>
          <div>
            <div className="text-[10px] text-gray-500 mb-0.5">Vol</div>
            <div className="text-xs font-bold text-[#00E0FF] mono">{sig.volume_ratio}x</div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <Zap className="w-3 h-3 text-[#F59E0B] shrink-0" />
            <span className="text-[10px] text-[#F59E0B] truncate">{sig.key_catalyst}</span>
          </div>
          {isAuthenticated && (
            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                data-testid={`hide-symbol-${sig.symbol}`}
                size="sm"
                variant="ghost"
                title="Hide this symbol for 24h"
                onClick={(e) => { e.stopPropagation(); onHide && onHide(sig.symbol); }}
                className="text-[10px] px-1.5 py-1 h-auto text-gray-500 hover:text-[#F59E0B] hover:bg-[#F59E0B]/10"
              >
                <EyeOff className="w-3 h-3" />
              </Button>
              <Button
                data-testid={`take-day-trade-${sig.style}-${sig.symbol}`}
                size="sm"
                onClick={(e) => { e.stopPropagation(); onTakeTrade(sig); }}
                className={`text-[10px] px-2 py-1 h-auto ${
                  isBuy
                    ? "bg-[#00FFA3]/20 text-[#00FFA3] hover:bg-[#00FFA3]/30 border border-[#00FFA3]/30"
                    : "bg-[#FF3B30]/20 text-[#FF3B30] hover:bg-[#FF3B30]/30 border border-[#FF3B30]/30"
                }`}
              >
                Take Trade
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DayTradingPanel({
  data,
  loading,
  error,
  isAuthenticated,
  onSignIn,
  onSubscribe,
  onTakeTrade,
  navigate,
  watchlistOnly,
  onToggleWatchlistOnly,
  hiddenSymbols = [],
  onHideSymbol,
  onUnhideSymbol,
  muteSuggestions = [],
  onAcceptSuggestion,
}) {
  const [styleFilter, setStyleFilter] = useState("all");        // all | scalp | day | swing
  const [directionFilter, setDirectionFilter] = useState("all"); // all | buy | sell | strong

  const allSignals = useMemo(() => {
    if (!data) return [];
    return [...(data.scalp_signals || []), ...(data.day_signals || []), ...(data.swing_signals || [])];
  }, [data]);

  const filteredSignals = useMemo(() => {
    let list = allSignals;
    if (styleFilter !== "all") list = list.filter((s) => s.style === styleFilter);
    if (directionFilter === "buy") list = list.filter((s) => s.signal.includes("BUY"));
    else if (directionFilter === "sell") list = list.filter((s) => s.signal.includes("SELL"));
    else if (directionFilter === "strong") list = list.filter((s) => s.conviction === "STRONG");
    // Sort by win_probability * expected_return desc
    return [...list].sort((a, b) => b.win_probability * b.expected_return - a.win_probability * a.expected_return);
  }, [allSignals, styleFilter, directionFilter]);

  if (error === "signin") {
    return (
      <Card className="glass-effect border-[#F59E0B]/20">
        <CardContent className="text-center py-12">
          <Lock className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Sign in to access Day Trading</h3>
          <p className="text-sm text-gray-400 mb-6">216+ live signals updated in real-time</p>
          <Button
            data-testid="day-trading-signin-btn"
            onClick={onSignIn}
            className="bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black font-bold"
          >
            Sign in
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (error === "payment") {
    return (
      <Card className="glass-effect border-[#F59E0B]/20">
        <CardContent className="text-center py-12">
          <Lock className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Unlock the Day Trading Engine</h3>
          <p className="text-sm text-gray-400 mb-6">Multiple signals per asset across scalp, day, and swing styles</p>
          <Button
            data-testid="day-trading-subscribe-btn"
            onClick={onSubscribe}
            className="bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black font-bold"
          >
            Subscribe to unlock
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (loading || !data) {
    return <div className="text-center py-12 text-gray-500" data-testid="day-trading-loading">Scanning 100 assets across scalp / day / swing styles...</div>;
  }

  return (
    <div className="space-y-5">
      {/* Watchlist toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button
            data-testid="watchlist-only-toggle"
            size="sm"
            variant={watchlistOnly ? "default" : "outline"}
            onClick={() => onToggleWatchlistOnly && onToggleWatchlistOnly(!watchlistOnly)}
            className={`text-xs ${watchlistOnly
              ? "bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30 hover:bg-[#F59E0B]/30"
              : "border-white/10 text-gray-400 hover:text-white"}`}
          >
            <Star className={`w-3.5 h-3.5 mr-1.5 ${watchlistOnly ? "fill-[#F59E0B]" : ""}`} />
            {watchlistOnly ? "Watchlist Only" : "All Assets"}
          </Button>
          {watchlistOnly && !data.watchlist_empty && (
            <span className="text-xs text-gray-500">
              Scanning your {data.stats.assets_scanned} watchlist {data.stats.assets_scanned === 1 ? "asset" : "assets"}
            </span>
          )}
        </div>
      </div>

      {/* Smart-mute suggestion banner */}
      {muteSuggestions.length > 0 && (
        <Card data-testid="smart-mute-banner" className="glass-effect border-[#F59E0B]/20 bg-[#F59E0B]/5">
          <CardContent className="p-3 sm:p-4 flex items-start gap-3">
            <Flame className="w-4 h-4 text-[#F59E0B] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-xs sm:text-sm text-white mb-2">
                <span className="font-semibold text-[#F59E0B]">Smart Mute:</span>{" "}
                You've muted these {muteSuggestions.length === 1 ? "this" : "these"} repeatedly — tap to mute for 30 days:
              </div>
              <div className="flex flex-wrap gap-1.5">
                {muteSuggestions.map((s) => (
                  <button
                    key={s.symbol}
                    data-testid={`suggest-permanent-${s.symbol}`}
                    onClick={() => onAcceptSuggestion && onAcceptSuggestion(s.symbol)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/30 hover:bg-[#F59E0B]/30 transition-colors"
                    title={`Muted ${s.count}x in last 7 days`}
                  >
                    {s.symbol}
                    <span className="text-[9px] opacity-70">×{s.count}</span>
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hidden symbols strip */}
      {hiddenSymbols.length > 0 && (
        <div
          data-testid="hidden-symbols-strip"
          className="glass-effect border border-white/5 rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap"
        >
          <EyeOff className="w-3.5 h-3.5 text-gray-500 shrink-0" />
          <span className="text-[11px] text-gray-500 uppercase tracking-wider">Muted (24h):</span>
          {hiddenSymbols.map((h) => (
            <button
              key={h.symbol}
              data-testid={`unhide-${h.symbol}`}
              onClick={() => onUnhideSymbol && onUnhideSymbol(h.symbol)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-white/5 text-gray-300 hover:bg-[#00FFA3]/10 hover:text-[#00FFA3] transition-colors"
              title="Unhide"
            >
              {h.symbol}
              <span className="text-gray-500 group-hover:text-[#00FFA3]">×</span>
            </button>
          ))}
        </div>
      )}

      {data.watchlist_empty ? (
        <Card className="glass-effect border-[#F59E0B]/20" data-testid="watchlist-empty-state">
          <CardContent className="text-center py-10">
            <Star className="w-10 h-10 text-[#F59E0B] mx-auto mb-3 opacity-60" />
            <h3 className="text-lg font-bold text-white mb-1">Your watchlist is empty</h3>
            <p className="text-sm text-gray-400 mb-4">
              Star tickers from the Stocks/Cryptos tabs to focus the engine on your favorites.
            </p>
            <Button
              size="sm"
              onClick={() => onToggleWatchlistOnly && onToggleWatchlistOnly(false)}
              className="bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/30 hover:bg-[#F59E0B]/30"
            >
              Show all assets
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {[
          { key: "total", label: "Total Signals", value: data.stats.total_signals, color: "text-white" },
          { key: "scalp", label: "Scalp", value: data.stats.scalp_count, color: "text-[#FF3B30]" },
          { key: "day",   label: "Day",   value: data.stats.day_count,   color: "text-[#F59E0B]" },
          { key: "swing", label: "Swing", value: data.stats.swing_count, color: "text-[#00E0FF]" },
        ].map((s) => (
          <Card key={s.key} className="glass-effect border-white/5">
            <CardContent className="p-3 text-center">
              <div className={`text-xl sm:text-2xl font-bold mono ${s.color}`} data-testid={`stat-${s.key}`}>{s.value}</div>
              <div className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider mt-0.5">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Momentum Scanner */}
      <div>
        <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-2">
          <Flame className="w-4 h-4 text-[#F59E0B]" />
          Momentum Scanner
        </h3>
        <MomentumStrip data={data.momentum_scanner} navigate={navigate} />
      </div>

      {/* Filters */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {[
            { key: "all",   label: `All Styles (${data.stats.total_signals})`, active: "bg-white/15 text-white border-white/30" },
            { key: "scalp", label: `Scalp (${data.stats.scalp_count})`,         active: "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30" },
            { key: "day",   label: `Day (${data.stats.day_count})`,             active: "bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30" },
            { key: "swing", label: `Swing (${data.stats.swing_count})`,         active: "bg-[#00E0FF]/20 text-[#00E0FF] border-[#00E0FF]/30" },
          ].map((f) => (
            <Button
              key={f.key}
              data-testid={`day-style-filter-${f.key}`}
              size="sm"
              variant={styleFilter === f.key ? "default" : "outline"}
              onClick={() => setStyleFilter(f.key)}
              className={`text-xs ${styleFilter === f.key ? f.active : "border-white/10 text-gray-400 hover:text-white"}`}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: "all", label: "All directions" },
            { key: "buy", label: `Buy (${data.stats.buy_count})` },
            { key: "sell", label: `Sell (${data.stats.sell_count})` },
            { key: "strong", label: `Strong only (${data.stats.strong_count})` },
          ].map((f) => (
            <Button
              key={f.key}
              data-testid={`day-direction-filter-${f.key}`}
              size="sm"
              variant={directionFilter === f.key ? "default" : "outline"}
              onClick={() => setDirectionFilter(f.key)}
              className={`text-xs ${directionFilter === f.key
                ? "bg-white/10 text-white border-white/30"
                : "border-white/10 text-gray-400 hover:text-white"}`}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Signals list */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="day-trading-signals-list">
        {filteredSignals.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-500">No signals match your filters</div>
        ) : (
          filteredSignals.map((sig, i) => (
            <motion.div
              key={`${sig.style}-${sig.symbol}-${i}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.01, 0.3) }}
            >
              <SignalRow sig={sig} isAuthenticated={isAuthenticated} onTakeTrade={onTakeTrade} onNavigate={navigate} onHide={onHideSymbol} boostTtlHours={data.boost_ttl_hours} />
            </motion.div>
          ))
        )}
      </div>

      <div className="text-center pt-2">
        <p className="text-xs text-gray-600">
          Generated at {new Date(data.generated_at).toLocaleTimeString()} · {data.stats.assets_scanned} assets scanned
        </p>
      </div>
        </>
      )}
    </div>
  );
}
